'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import Box from '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';
import SystemIcon from '../../DataDisplays/SystemIcon/SystemIcon.js';
import TextFieldContainer from '../TextFieldContainer/TextFieldContainer.js';
import SupportTextContainer from '../SupportTextContainer/SupportTextContainer.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
/**
 * 사용자가 여러 줄의 텍스트를 입력할 수 있는 textarea 컴포넌트입니다.
 *
 * @param {Object} props
 * @param {boolean} [props.isError] - 에러 상태를 표시합니다
 * @param {('sm'|'md'|'lg')} [props.size='md'] - textarea의 크기를 지정합니다
 * @param {boolean} [props.resize=false] - 크기 조절 가능 여부를 설정합니다
 * @param {string} [props.supportText] - 도움말 텍스트를 표시합니다
 * @param {string} [props.errorText] - 에러 메시지를 표시합니다
 * @param {boolean} [props.isLengthVisible=false] - 글자 수 표시 여부를 설정합니다
 * @param {React.ComponentProps<'textarea'>} props.textareaProps - textarea 엘리먼트의 기본 속성들
 *
 * @example
 * ```tsx
 * // 기본 사용
 * <Textarea
 *   placeholder="내용을 입력하세요"
 *   onChange={(e) => console.log(e.target.value)}
 * />
 *
 * // 에러 상태와 에러 메시지
 * <Textarea
 *   isError={true}
 *   errorText="필수 입력 항목입니다"
 * />
 *
 * // 크기 조절 가능하고 글자 수 제한이 있는 경우
 * <Textarea
 *   resize={true}
 *   maxLength={1000}
 *   isLengthVisible={true}
 *   supportText="최대 1000자까지 입력 가능합니다"
 * />
 * ```
 */ const Textarea = (_param)=>{
    var { isError = false, size = 'md', resize = false, isLengthVisible = false, supportText, errorText, maxLength, value, disabled, name, onChange } = _param, rest = _object_without_properties(_param, [
        "isError",
        "size",
        "resize",
        "isLengthVisible",
        "supportText",
        "errorText",
        "maxLength",
        "value",
        "disabled",
        "name",
        "onChange"
    ]);
    const [isFocused, setIsFocused] = useState(false);
    const isSupportContainerVisible = isError || supportText || isLengthVisible;
    const generateHeight = ()=>{
        if (size === 'sm') return 'min-h-[118px]';
        if (size === 'md') return 'min-h-[238px]';
        if (size === 'lg') return 'min-h-[358px]';
    };
    const onFieldFocus = (e)=>{
        setIsFocused(true);
    };
    const onFieldBlur = (e)=>{
        setIsFocused(false);
    };
    return /*#__PURE__*/ jsxs(Box, {
        "data-testid": "design-system-textarea",
        className: "flex h-fit w-full flex-col gap-2",
        children: [
            /*#__PURE__*/ jsxs(TextFieldContainer, {
                isDisabled: disabled,
                isError: isError,
                isFocused: isFocused,
                isTextarea: true,
                children: [
                    /*#__PURE__*/ jsx("textarea", _object_spread({
                        "data-testid": "design-system-textarea--textarea",
                        className: twMerge('w-full overflow-y-scroll py-[11px] pl-4 font-[inherit] font-normal tracking-default text-w-gray-600 outline-none scrollbar-list placeholder:font-[inherit] placeholder:tracking-default placeholder:text-w-gray-300', generateHeight(), resize ? 'resize-y' : 'resize-none', disabled && 'resize-none'),
                        name: name,
                        value: value,
                        disabled: disabled,
                        onChange: onChange,
                        maxLength: maxLength,
                        onFocus: onFieldFocus,
                        onBlur: onFieldBlur
                    }, rest)),
                    resize && /*#__PURE__*/ jsx(SystemIcon, {
                        name: "small_resizer",
                        className: "absolute bottom-[5px] right-[5px] text-w-gray-400",
                        testId: "design-system-textarea--resize-icon"
                    })
                ]
            }),
            isSupportContainerVisible && /*#__PURE__*/ jsx(SupportTextContainer, {
                errorMessage: errorText,
                supportMessage: supportText,
                currentLength: (value === null || value === void 0 ? void 0 : value.toString().length) || 0,
                maxLength: maxLength,
                isMaxLengthVisible: isLengthVisible
            })
        ]
    });
};

export { Textarea as default };
